import type { Condition, ConditionGroup, RuleConditions } from '@mockd/shared/types/rule';
import { isConditionGroup } from '@mockd/shared/utils';

/** Collect every leaf condition in a conditions tree, for summaries and badges. */
export function flattenConditions(conditions: RuleConditions | undefined): Condition[] {
  if (!conditions) return [];
  if (Array.isArray(conditions)) return conditions;
  const leaves: Condition[] = [];
  const stack = [...conditions.conditions];
  while (stack.length > 0) {
    const node = stack.shift()!;
    if (isConditionGroup(node)) {
      stack.unshift(...node.conditions);
    } else {
      leaves.push(node);
    }
  }
  return leaves;
}

/**
 * Find a `method equals` condition that provably constrains the rule: only
 * traverse non-negated conjunctive contexts — flat lists, AND groups, and
 * single-child groups (where OR degenerates to AND). A method leaf inside an
 * OR branch or a negated group does NOT restrict the rule to that method.
 */
export function findEffectiveMethodCondition(
  conditions: RuleConditions | undefined,
): Condition | undefined {
  if (!conditions) return undefined;
  const nodes = Array.isArray(conditions)
    ? conditions
    : isConjunctive(conditions) ? conditions.conditions : [];
  for (const node of nodes) {
    if (isConditionGroup(node)) {
      const found = isConjunctive(node) ? findEffectiveMethodCondition(node) : undefined;
      if (found) return found;
    } else if (node.target === 'method' && node.operator === 'equals') {
      return node;
    }
  }
  return undefined;
}

function isConjunctive(group: ConditionGroup): boolean {
  return !group.negate && (group.logic === 'and' || group.conditions.length <= 1);
}

export type EditableConditions =
  | { editable: true; mode: 'all' | 'any'; conditions: Condition[] }
  | { editable: false };

/**
 * The simple editor handles a flat list (match all) or a single-level group
 * (match all/any). Nested groups and negation must be edited via the API.
 */
export function parseEditableConditions(conditions: RuleConditions | undefined): EditableConditions {
  if (!conditions) return { editable: true, mode: 'all', conditions: [] };
  if (Array.isArray(conditions)) return { editable: true, mode: 'all', conditions };
  if (conditions.negate) return { editable: false };
  const flat: Condition[] = [];
  for (const node of conditions.conditions) {
    if (isConditionGroup(node)) return { editable: false };
    flat.push(node);
  }
  return { editable: true, mode: conditions.logic === 'or' ? 'any' : 'all', conditions: flat };
}

/** Build the API shape from simple-editor state. Flat lists stay flat (AND shorthand). */
export function buildConditions(mode: 'all' | 'any', conditions: Condition[]): RuleConditions | undefined {
  if (conditions.length === 0) return undefined;
  if (mode === 'any' && conditions.length > 1) return { logic: 'or', conditions };
  return conditions;
}
